import { memo, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import "./UpdateEmployeeModal.css";
import defaultEmployee from "../../assets/icon_employee.jpg";
import { firebaseUploadEmployeeImage } from "../../services/firebaseUpload";
import { serviceEmployeePutUpdate } from "../../api/serviceFleetMaster";

const normalizeEmployee = (employee) => ({
  vehicleId: employee.vehicleId ?? employee.vehicle?._id ?? null,

  name: employee.name ?? "",
  email: employee.email ?? "",
  phone: employee.phone ?? "",
  passcode: employee.passcode ?? "",
  imageUrl: employee.imageUrl ?? "",
});

const selectStyles = {
  menuPortal: (baseStyles) => ({
    ...baseStyles,
    zIndex: 9999,
  }),
};

const UpdateEmployeeModal = ({
  employee,
  onClose,
  onUpdate,
  vehicleOptions = [],
}) => {
  const fileInputRef = useRef(null);

  const [image, setImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");

  const [updating, setUpdating] = useState(false);

  const [originalEmployee, setOriginalEmployee] = useState(null);

  const [updatedEmployee, setUpdatedEmployee] = useState(null);

  /*
   * Reset the form whenever a different employee is opened.
   */
  useEffect(() => {
    if (!employee) {
      setOriginalEmployee(null);
      setUpdatedEmployee(null);
      setImage(null);
      return;
    }

    const normalizedEmployee = normalizeEmployee(employee);

    setOriginalEmployee(normalizedEmployee);
    setUpdatedEmployee(normalizedEmployee);
    setImage(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [employee]);

  /*
   * Create and clean up the temporary image-preview URL.
   */
  useEffect(() => {
    if (!image) {
      setImagePreviewUrl("");
      return undefined;
    }

    const previewUrl = URL.createObjectURL(image);

    setImagePreviewUrl(previewUrl);

    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [image]);

  const selectedVehicleOption = useMemo(() => {
    if (!updatedEmployee?.vehicleId) {
      return null;
    }

    return (
      vehicleOptions.find(
        (option) => String(option.value) === String(updatedEmployee.vehicleId),
      ) || null
    );
  }, [vehicleOptions, updatedEmployee?.vehicleId]);

  const hasChanges = useMemo(() => {
    if (!originalEmployee || !updatedEmployee) {
      return false;
    }

    if (image) {
      return true;
    }

    return Object.keys(originalEmployee).some(
      (key) => originalEmployee[key] !== updatedEmployee[key],
    );
  }, [originalEmployee, updatedEmployee, image]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setUpdatedEmployee((currentEmployee) => ({
      ...currentEmployee,
      [name]: value,
    }));
  };

  const handleVehicleChange = (option) => {
    setUpdatedEmployee((currentEmployee) => ({
      ...currentEmployee,
      vehicleId: option?.value ?? null,
    }));
  };

  const handleImageChange = (event) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    if (!selectedFile.type.startsWith("image/")) {
      toast.error("Please select a valid image file.");

      event.target.value = "";
      return;
    }

    setImage(selectedFile);
  };

  const openImagePicker = () => {
    if (updating) {
      return;
    }

    fileInputRef.current?.click();
  };

  const validateEmployee = () => {
    if (!employee?._id) {
      throw new Error("Invalid employee.");
    }

    if (!updatedEmployee?.name.trim()) {
      throw new Error("Employee name is required.");
    }

    if (
      updatedEmployee.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(updatedEmployee.email.trim())
    ) {
      throw new Error("Enter a valid email address.");
    }
  };

  const serverEmployeeUpdate = async () => {
    if (!hasChanges) {
      toast.info("Employee is already up to date.");
      return;
    }

    try {
      validateEmployee();
      setUpdating(true);

      const imageUrl = null;
      // const imageUrl = image
      //   ? await firebaseUploadEmployeeImage({
      //       organizationId,
      //       file: image,
      //     })
      //   : updatedEmployee.imageUrl;

      const payload = {
        vehicleId: updatedEmployee.vehicleId || null,

        name: updatedEmployee.name.trim() || null,

        email: updatedEmployee.email.trim() || null,

        phone: updatedEmployee.phone.trim() || null,

        passcode: String(updatedEmployee.passcode).trim() || null,

        imageUrl: imageUrl || null,
      };

      if (import.meta.env.DEV) {
        console.log("Update employee payload:", payload);
      }

      await serviceEmployeePutUpdate(employee._id, payload);

      toast.success("Employee updated successfully!");

      await onUpdate?.();
      onClose();
    } catch (error) {
      console.error("Update employee error:", error);

      toast.error(
        error instanceof Error ? error.message : "Error: Contact Trucktronix!",
      );
    } finally {
      setUpdating(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await serverEmployeeUpdate();
  };

  if (!updatedEmployee) {
    return null;
  }

  const displayedImage =
    imagePreviewUrl || updatedEmployee.imageUrl || defaultEmployee;

  const menuPortalTarget =
    typeof document !== "undefined" ? document.body : null;

  return (
    <div className="uem-shell">
      <button
        type="button"
        className="uem-close-btn"
        onClick={onClose}
        disabled={updating}
        aria-label="Close update employee form"
      >
        <i className="bi bi-x-lg" />
      </button>

      <div className="uem-card">
        <div className="uem-top-row">
          <div className="uem-header">
            <div className="uem-photo-section">
              <button
                type="button"
                className="uem-image-wrap"
                onClick={openImagePicker}
                disabled={updating}
                aria-label="Change employee photo"
              >
                <img
                  src={displayedImage}
                  alt={updatedEmployee.name || "Employee"}
                />

                <span className="uem-image-overlay">
                  <i className="bi bi-camera-fill" />
                </span>
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleImageChange}
              />
            </div>

            <div className="uem-header-info">
              <span className="uem-title">Update Employee</span>

              <p className="uem-subtitle">
                Edit profile, contact and vehicle assignment details
              </p>

              <div className="uem-meta">
                <p>
                  <span>Employee ID</span>

                  <span className="uem-meta-value">
                    {employee.employeeCode || "—"}
                  </span>
                </p>

                <p>
                  <span>Status</span>

                  <span className="uem-meta-value">
                    {hasChanges ? "Unsaved changes" : "Up to date"}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>

        <form
          id="update-employee-form"
          className="uem-data-area"
          onSubmit={handleSubmit}
          noValidate
        >
          <FormSection title="Assignment" icon="bi-truck">
            <FormField
              label="Assigned Vehicle"
              htmlFor="uem-vehicle-select"
              wide
            >
              <Select
                inputId="uem-vehicle-select"
                className="uem-select"
                classNamePrefix="uem-select"
                options={vehicleOptions}
                value={selectedVehicleOption}
                onChange={handleVehicleChange}
                isClearable
                isDisabled={updating}
                menuPortalTarget={menuPortalTarget}
                menuPosition="fixed"
                placeholder="Select vehicle..."
                noOptionsMessage={() => "No vehicles available"}
                styles={selectStyles}
              />
            </FormField>
          </FormSection>

          <FormSection title="Profile" icon="bi-person">
            <FormField label="Employee Name" htmlFor="uem-name" required>
              <input
                id="uem-name"
                type="text"
                name="name"
                value={updatedEmployee.name}
                onChange={handleChange}
                disabled={updating}
                autoComplete="name"
                placeholder="Employee name"
                required
              />
            </FormField>

            <FormField label="Email" htmlFor="uem-email">
              <input
                id="uem-email"
                type="email"
                name="email"
                value={updatedEmployee.email}
                onChange={handleChange}
                disabled={updating}
                autoComplete="email"
                placeholder="name@example.com"
              />
            </FormField>
          </FormSection>

          <FormSection title="Security & Contact" icon="bi-shield-lock">
            <FormField label="Phone" htmlFor="uem-phone">
              <input
                id="uem-phone"
                type="tel"
                name="phone"
                value={updatedEmployee.phone}
                onChange={handleChange}
                disabled={updating}
                autoComplete="tel"
                placeholder="Phone number"
              />
            </FormField>

            <FormField label="Passcode" htmlFor="uem-passcode" sensitive>
              <input
                id="uem-passcode"
                type="text"
                name="passcode"
                value={updatedEmployee.passcode}
                onChange={handleChange}
                disabled={updating}
                inputMode="numeric"
                autoComplete="off"
                placeholder="Employee passcode"
              />
            </FormField>
          </FormSection>
        </form>
      </div>

      <footer className="uem-footer">
        <div className="uem-footer-inner">
          <div className="uem-actions">
            <button
              type="submit"
              form="update-employee-form"
              className="uem-submit-cta"
              disabled={updating}
            >
              {updating ? (
                <>
                  <span className="uem-spinner" aria-hidden="true" />

                  <span>Updating...</span>
                </>
              ) : (
                <span>Save Changes</span>
              )}
            </button>

            <button
              type="button"
              className="uem-cancel-btn"
              onClick={onClose}
              disabled={updating}
            >
              Cancel
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

const FormSection = ({ title, icon, children }) => (
  <section className="uem-section">
    <header>
      <i className={`bi ${icon}`} />
      <span>{title}</span>
    </header>

    <div className="uem-grid">{children}</div>
  </section>
);

const FormField = ({
  label,
  htmlFor,
  required = false,
  wide = false,
  sensitive = false,
  children,
}) => (
  <div
    className={["uem-field", wide ? "wide" : "", sensitive ? "sensitive" : ""]
      .filter(Boolean)
      .join(" ")}
  >
    <label htmlFor={htmlFor}>
      <span>{label}</span>

      {required && (
        <span className="uem-required" aria-hidden="true">
          *
        </span>
      )}
    </label>

    <div className="uem-control">{children}</div>
  </div>
);

export default memo(UpdateEmployeeModal);
